import { Button, Grid, Table, Tag, Typography } from 'antd'
import { AttachmentUpload } from '@/features/files/index.ts'
import { formatAmount } from '@/utils/money.ts'
import { billDate } from './bill-search.ts'
import { BILL_KIND_COLORS, BILL_KINDS, BILL_STATUS, BILL_STATUS_COLORS } from './const/bill-search.ts'
import type { MoneyDetailData } from './finance.types.ts'

type MoneyAllocation = MoneyDetailData['allocations'][number]

export function MoneyAllocationsTable({
  allocations,
  onBill
}: {
  allocations: MoneyDetailData['allocations']
  onBill?: (id: number) => void
}) {
  const screens = Grid.useBreakpoint()
  return (
    <Table
      size="small"
      tableLayout="fixed"
      scroll={{ x: '58rem' }}
      classNames={{
        header: { cell: 'text-content-muted! px-4! py-2.5! text-xs! font-medium!' },
        body: { cell: 'px-4! py-2.5! align-top' }
      }}
      rowKey="id"
      dataSource={allocations}
      pagination={false}
      locale={{ emptyText: '暂无账单分配' }}
      columns={[
        {
          title: '账单 ID',
          dataIndex: 'billId',
          width: '5.5rem',
          render: (billId: number) => (
            <Typography.Text copyable className="tabular-nums">
              {billId}
            </Typography.Text>
          )
        },
        {
          title: '账单信息',
          key: 'bill',
          render: (_, allocation) => <AllocationBillSummary allocation={allocation} onBill={onBill} />
        },
        {
          title: '分配备注',
          dataIndex: 'note',
          width: '8rem',
          render: (value) => <span className="text-content-muted break-words whitespace-pre-wrap">{value || '—'}</span>
        },
        {
          title: '截图／回单',
          key: 'attachments',
          width: '9rem',
          render: (_, row) => {
            if (!row.attachmentIds.length) {
              return <span className="text-content-soft">—</span>
            }
            return <AttachmentUpload purpose="moneyAttachment" readOnly listType="text" value={row.attachmentIds} />
          }
        },
        {
          title: '分配金额',
          dataIndex: 'amount',
          align: 'right',
          fixed: screens.sm ? 'right' : undefined,
          width: '10.5rem',
          render: (value, allocation) => (
            <div className="space-y-1.5">
              <p className="font-semibold whitespace-nowrap tabular-nums">¥{formatAmount(value)}</p>
              {allocation.billSummary.totalAmount != null && (
                <p className="text-content-muted text-xs tabular-nums">
                  账单 ¥{formatAmount(allocation.billSummary.totalAmount)}
                </p>
              )}
              <p className="text-content-soft text-xs leading-4">
                分配 #{allocation.id}
                {allocation.refundOfAllocationId && (
                  <span className="block">原分配 #{allocation.refundOfAllocationId}</span>
                )}
              </p>
            </div>
          )
        }
      ]}
    />
  )
}

function AllocationBillSummary({ allocation, onBill }: { allocation: MoneyAllocation; onBill?: (id: number) => void }) {
  const bill = allocation.billSummary
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Typography.Text copyable={{ text: allocation.billNo }}>
          {onBill ? (
            <Button
              type="link"
              className="h-auto! max-w-full! p-0! text-left! whitespace-normal!"
              onClick={() => onBill(allocation.billId)}
            >
              <span className="break-all">{allocation.billNo}</span>
            </Button>
          ) : (
            <span className="break-all">{allocation.billNo}</span>
          )}
        </Typography.Text>
        <Tag className="m-0!" color={BILL_KIND_COLORS[bill.feeType]}>
          {BILL_KINDS[bill.feeType]}
        </Tag>
        <Tag className="m-0!" color={BILL_STATUS_COLORS[bill.status]}>
          {BILL_STATUS[bill.status].text}
        </Tag>
      </div>
      <p className="text-sm leading-5 break-words">
        <span className="font-medium">{bill.businessName || bill.expenseBusinessName || '未关联业务'}</span>
        <span className="text-content-muted"> · {bill.description}</span>
      </p>
      <div className="text-content-muted flex flex-wrap gap-x-3 gap-y-1 text-xs leading-4 tabular-nums">
        <span>
          账期 {billDate(bill.periodStart)} 至 {billDate(bill.periodEnd)}
        </span>
        {bill.dueAt && <span>到期 {billDate(bill.dueAt)}</span>}
      </div>
      <div className="text-content-soft flex flex-wrap gap-x-3 gap-y-1 text-xs leading-4 break-words">
        <span>来源 {bill.sourceOrderNo || '独立账单'}</span>
        {bill.siteName && <span>场地 {bill.siteName}</span>}
        {bill.salespersonName && <span>业务员 {bill.salespersonName}</span>}
      </div>
    </div>
  )
}
